/**
 * SH-INFRA-005 — user-facing fallback rendered by <ErrorBoundary> when an
 * uncaught render error bubbles up. Pairs with `react-error-boundary` which
 * supplies the `FallbackProps` contract ({ error, resetErrorBoundary }).
 *
 * Design notes:
 * - Stack traces are rendered only when `import.meta.env.DEV` is truthy. Vite
 *   statically replaces this at build time, so the `<pre>` path is tree-shaken
 *   out of production bundles entirely (verified by the build-time AC).
 * - Recovery actions: "Try Again" calls `resetErrorBoundary()` to remount the
 *   failed subtree; "Go Home" navigates to `/` via react-router's `useNavigate`
 *   when a Router is present, falling back to `window.location.assign('/')`
 *   when the fallback is rendered at the absolute root (outside RouterProvider).
 */
import type { FallbackProps } from 'react-error-boundary';
import { useNavigate, useInRouterContext } from 'react-router-dom';

import { Button } from '@dfq/shared/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@dfq/shared/components/ui/card';
import { cn } from '@dfq/shared/lib/utils';

export interface ErrorFallbackProps extends FallbackProps {
  /**
   * Optional extra className forwarded to the root wrapper. Exposed so that
   * nested boundaries (admin / account zones) can tighten margins or align
   * with their layout shell without forking the component.
   */
  className?: string;
}

// Router-aware "Go Home" button. Splitting this out keeps useNavigate at the
// top of a component that only renders when a Router ancestor exists, which
// honours the Rules of Hooks while still supporting the root-level boundary.
function GoHomeButtonInRouter() {
  const navigate = useNavigate();
  return (
    <Button onClick={() => navigate('/')} variant="outline">
      Go Home
    </Button>
  );
}

function GoHomeButtonHardRedirect() {
  return (
    <Button onClick={() => window.location.assign('/')} variant="outline">
      Go Home
    </Button>
  );
}

function GoHomeButton() {
  const inRouter = useInRouterContext();
  return inRouter ? <GoHomeButtonInRouter /> : <GoHomeButtonHardRedirect />;
}

export function ErrorFallback({ error, resetErrorBoundary, className }: ErrorFallbackProps) {
  const errorMessage =
    error instanceof Error ? error.message : 'An unexpected error occurred.';

  return (
    <div
      role="alert"
      className={cn(
        'flex min-h-[60vh] w-full items-center justify-center p-6',
        className,
      )}
    >
      <Card className="w-full max-w-xl">
        <CardHeader>
          <CardTitle>Something went wrong</CardTitle>
          <CardDescription>
            An unexpected error interrupted this page. You can try again, or
            head back to the homepage.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">{errorMessage}</p>

          {import.meta.env.DEV && error instanceof Error && error.stack ? (
            <pre
              data-testid="error-fallback-stack"
              className="max-h-64 overflow-auto rounded-md border border-border bg-muted p-3 text-xs text-muted-foreground"
            >
              {error.stack}
            </pre>
          ) : null}
        </CardContent>
        <CardFooter className="flex flex-wrap gap-2">
          <Button onClick={() => resetErrorBoundary()} variant="default">
            Try Again
          </Button>
          <GoHomeButton />
        </CardFooter>
      </Card>
    </div>
  );
}
